/**
 * Job DataLayer Utilities
 *
 * PURPOSE:
 * Convert job data from your system into GA4's expected format.
 * GA4 uses an "ecommerce" model even for non-ecommerce sites.
 * Jobs are treated as "products" in GA4's mental model.
 *
 * GA4 ITEM STRUCTURE:
 * - item_id: Unique identifier (your cockpitId or entry ID)
 * - item_name: Human-readable name (job title)
 * - item_category: Primary category (sector)
 * - item_category2-5: Additional categorization
 * - price: Numeric value (0 for jobs)
 * - quantity: Always 1 for jobs
 * - affiliation: Office/branch name
 * - index: Position in list (for list views)
 */

import { pushEcommerceEvent, pushEvent } from "./datalayer"

/**
 * The GA4 item format
 * This matches what GA4 expects in the items[] array
 */
export interface GA4JobItem {
    item_id: string;
    item_name: string;
    item_category: string;          // Sector
    item_category2?: string;        // City
    item_category3?: string;        // Contract type
    item_category4?: string;        // Work regime
    item_category5?: string;        // Shift
    price: number;
    quantity: number;
    affiliation: string;            // Office name
    index?: number;                 // Position in list
    item_list_id?: string;          // List context
    item_list_name?: string;        // List context name
}

/**
 * User data for Enhanced Conversions
 * Supports both raw (for sGTM hashing) and pre-hashed formats
 */
export interface UserData {
    // Raw format (sGTM will hash)
    email?: string;
    phone_number?: string;
    // Pre-hashed format (client-side hashed)
    sha256_email_address?: string;
    sha256_phone_number?: string;
    // Address data
    address?: {
        first_name?: string;
        last_name?: string;
        street?: string;
        city?: string;
        postal_code?: string;
        country?: string;
    };
}

/**
 * Typesense job document structure
 */
export interface TypesenseJob {
    id: string;
    reference?: string;
    title: string;
    city?: string;
    sector_full?: Array<{ id: string; title: string }>;
    contract_type_full?: Array<{ id: string; title: string }>;
    work_regime_full?: Array<{ id: string; title: string }>;
    shift_full?: Array<{ id: string; title: string }>;
    office_slug?: string;
    url?: string;
}

/**
 * Craft CMS job entry structure
 * (what you get from Twig's entry variable)
 */
export interface CraftJob {
    id: number | string;
    cockpitId?: string;
    title: string;
    sector?: { title: string };
    contractType?: { title: string };
    employmentType?: { title: string };
    shift?: { title: string };
    department?: { title: string; slug: string };
    address?: { locality: string };
}

/**
 * Convert a Typesense job hit to GA4 item format
 *
 * Use this in Vue components that display job cards from search results
 *
 * @param job - The Typesense job document
 * @param index - Position in the list (0-based, we'll add 1)
 * @param listContext - Which list is this from?
 */
export function mapTypesenseJobToGA4Item(
    job: TypesenseJob,
    index?: number,
    listContext?: { id: string; name: string }
): GA4JobItem {
    return {
        item_id: job.reference || job.id,
        item_name: job.title,
        item_category: job.sector_full?.[0]?.title || 'Onbekend',
        item_category2: job.city || 'Onbekend',
        item_category3: job.contract_type_full?.[0]?.title || '',
        item_category4: job.work_regime_full?.[0]?.title || '',
        item_category5: job.shift_full?.[0]?.title || '',
        price: 0,
        quantity: 1,
        affiliation: 'Go4Jobs',  // Could be office name if needed
        ...(index !== undefined && { index: index + 1 }),  // GA4 uses 1-based index
        ...(listContext && {
            item_list_id: listContext.id,
            item_list_name: listContext.name
        })
    };
}

/**
 * Convert a Craft CMS job entry to GA4 item format
 *
 * Use this in Twig templates where you have the entry object
 * Called via window.__GO4JOBS_JOB_DATA__ (explained in Part 5)
 */
export function mapCraftJobToGA4Item(job: CraftJob): GA4JobItem {
    return {
        item_id: job.cockpitId || String(job.id),
        item_name: job.title,
        item_category: job.sector?.title || 'Onbekend',
        item_category2: job.address?.locality || 'Onbekend',
        item_category3: job.contractType?.title || '',
        item_category4: job.employmentType?.title || '',
        item_category5: job.shift?.title || '',
        price: 0,
        quantity: 1,
        affiliation: job.department?.title || 'Go4Jobs'
    };
}

/**
 * Event 1: view_item_list
 *
 * When: User sees a list of jobs (search results, category page, related jobs, ...)
 * @param items - Array of job items
 * @param listId - Identifier for the list type
 * @param listName - Human-readable list name
 * @param pagination - Current page info
 */
export function trackViewItemList(
    items: GA4JobItem[],
    listId: string,
    listName: string,
    pagination?: { currentPage: number; totalPages: number }
): void {
    pushEcommerceEvent('view_item_list', {
        item_list_id: listId,
        item_list_name: listName,
        items: items,
    }, pagination ? { pagination } : {})
}

/**
 * Event 1b: select_item
 *
 * When: User click on a job card to view details
 *
 * @param item - The clicked job
 * @param listId - Which list the job was in
 */
export function trackSelectItem(item: GA4JobItem, listId: string): void {
    pushEcommerceEvent('select_item', {
        item_list_id: listId,
        items: [item],
    })
}

/**
 * Event 2: view_item
 *
 * When: User views a job detail page
 *
 * @param item - Teh job being viewed
 */
export function trackViewItem(item: GA4JobItem): void {
    pushEcommerceEvent('view_item', {
        items: [item]
    })
}

/**
 * Event 3: search
 * WHEN: User searches or filters jobs
 *
 * @param searchTerm - What the user typed
 * @param filters - Applied filters
 */
export function trackSearch(
    searchTerm: string,
    filters: {
        location?: string;
        category?: string;
        job_shift?: string[];
        contract_type?: string[];
    }
): void {
    pushEvent('search', {
        search_term: searchTerm,
        search_filters: filters,
    })
}

/**
 * Event 4: add_to_cart
 *
 * When: User clicks apply button
 *
 * @param item - The job being applied to
 */
export function trackAddToCart(item: GA4JobItem): void {
    pushEcommerceEvent('add_to_cart', {
        items: [item]
    })
}

/**
 * Event 5: purchase
 *
 * When: User submits a job application
 *
 * @param transactionId - Unique ID for this application (submission ID)
 * @param item - The job applied to
 * @param userData - Applicant's info (raw or pre-hashed)
 *
 * User data can be:
 * - Raw: { email, phone_number } - sGTM will hash
 * - Hashed: { sha256_email_address, sha256_phone_number } - client-side hashed
 */
export function trackPurchase(
    transactionId: string,
    item: GA4JobItem,
    userData?: UserData
): void {
    pushEcommerceEvent('purchase', {
        transaction_id: transactionId,
        items: [item]
    }, userData ? { user_data: userData } : {});
}

/**
 * EVENT 5b: purchase (spontaneous application)
 *
 * WHEN: User submits a spontaneous application (no specific job)
 *
 * @param transactionId - Unique ID for this application
 * @param userData - Applicant's info (raw or pre-hashed)
 */
export function trackSpontaneousApplication(
    transactionId: string,
    userData?: UserData
): void {
    const spontaneousItem: GA4JobItem = {
        item_id: 'spontaneous',
        item_name: 'Spontane Sollicitatie',
        item_category: 'Spontaan',
        price: 0,
        quantity: 1,
        affiliation: 'Go4Jobs'
    };

    pushEcommerceEvent('purchase', {
        transaction_id: transactionId,
        items: [spontaneousItem]
    }, userData ? { user_data: userData } : {});
}

/**
 * EVENT 6: add_to_wishlist
 *
 * WHEN: User clicks the heart/favourite button
 *
 * @param item - The job being favourited
 */
export function trackAddToWishlist(item: GA4JobItem): void {
    pushEcommerceEvent('add_to_wishlist', {
        items: [item]
    });
}

/**
 * EVENT 7: sign_up
 *
 * WHEN: User creates an account
 */
export function trackSignUp(method: string = 'email'): void {
    pushEvent('sign_up', { method });
}

/**
 * EVENT 8: login
 *
 * WHEN: User logs in
 */
export function trackLogin(method: string = 'email'): void {
    pushEvent('login', { method });
}

/**
 * EVENT 10: generate_lead
 *
 * WHEN: Contact form submission, email link click
 *
 * @param leadType - Type of lead
 * @param office - Office name (for office-specific leads)
 */
export function trackGenerateLead(
    leadType: 'contact_office' | 'contact_go4jobs' | 'staffing_entrepreneur' | 'office_email_click',
    office?: string
): void {
    pushEvent('generate_lead', {
        lead_type: leadType,
        ...(office && { office })
    });
}

/**
 * EVENT 11: page_view (blog)
 *
 * WHEN: User views a blog article
 *
 * @param pageTitle - Article title
 * @param itemCategory - Related job category (for remarketing)
 */
export function trackBlogView(pageTitle: string, itemCategory?: string): void {
    pushEvent('page_view', {
        page_type: 'blog_article',
        page_title: pageTitle,
        ...(itemCategory && { item_category: itemCategory })
    }, false);  // Blog views don't require consent
}
